import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  createWindow, 
  closeWindow, 
  minimizeWindow, 
  maximizeWindow, 
  restoreWindow,
  focusWindow,
  arrangeWindows,
  saveLayout,
  applyLayout,
  reopenLastClosedWindow,
  clearAllWindows
} from '../store/slices/windowsSlice';
import { RootState } from '../store';
import logger from '../services/logger';

// Define shortcut key combinations
const SHORTCUTS = {
  NEW_WINDOW: { key: 'n', ctrlKey: true }, // Ctrl+N (or Cmd+N on Mac)
  CLOSE_WINDOW: { key: 'w', ctrlKey: true }, // Ctrl+W
  MINIMIZE_WINDOW: { key: 'm', altKey: true }, // Alt+M
  MAXIMIZE_WINDOW: { key: 'm', ctrlKey: true }, // Ctrl+M
  NEXT_WINDOW: { key: 'Tab', altKey: true }, // Alt+Tab
  PREVIOUS_WINDOW: { key: 'Tab', altKey: true, shiftKey: true }, // Alt+Shift+Tab
  CASCADE_WINDOWS: { key: 'ArrowDown', altKey: true, shiftKey: true }, // Alt+Shift+Down
  TILE_WINDOWS: { key: 'ArrowUp', altKey: true, shiftKey: true }, // Alt+Shift+Up
  HORIZONTAL_WINDOWS: { key: 'ArrowRight', altKey: true, shiftKey: true }, // Alt+Shift+Right
  VERTICAL_WINDOWS: { key: 'ArrowLeft', altKey: true, shiftKey: true }, // Alt+Shift+Left
  SAVE_LAYOUT: { key: 's', altKey: true, shiftKey: true }, // Alt+Shift+S
  APPLY_LAYOUT: { key: 'l', altKey: true, shiftKey: true }, // Alt+Shift+L
  REOPEN_LAST_CLOSED: { key: 'z', ctrlKey: true }, // Ctrl+Z
  SETTINGS_WINDOW: { key: ',', ctrlKey: true }, // Ctrl+,
  HELP_WINDOW: { key: 'F1' }, // F1
};

// Map number keys for window selection (1-9)
const NUMBER_KEYS = Array.from({ length: 9 }, (_, i) => String(i + 1));

/**
 * Hook for handling window-related keyboard shortcuts
 */
export function useWindowShortcuts() {
  const dispatch = useDispatch();
  const windows = useSelector((state: RootState) => state.windows.windows);
  const activeWindowId = useSelector((state: RootState) => state.windows.activeWindowId);
  const layouts = useSelector((state: RootState) => state.windows.layouts);
  const activeLayout = useSelector((state: RootState) => state.windows.activeLayout);
  const hasRecentlyClosed = useSelector((state: RootState) => 
    state.windows.recentlyClosedWindows.length > 0
  );
  
  // Get active window
  const activeWindow = activeWindowId ? windows[activeWindowId] : null;
  
  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Skip if inside input elements
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes((event.target as HTMLElement).tagName)) {
      return;
    }
    
    // Helper function to check if a shortcut matches
    const matchesShortcut = (shortcut: any) => {
      return (
        event.key === shortcut.key &&
        !!event.ctrlKey === !!shortcut.ctrlKey &&
        !!event.altKey === !!shortcut.altKey &&
        !!event.shiftKey === !!shortcut.shiftKey &&
        !!event.metaKey === !!shortcut.metaKey
      );
    };
    
    // Create new window (Ctrl+N)
    if (matchesShortcut(SHORTCUTS.NEW_WINDOW)) {
      event.preventDefault();
      dispatch(createWindow({
        title: 'New Window',
        type: 'editor',
        content: 'New window created with keyboard shortcut'
      }));
      logger.info('Created new window via shortcut (Ctrl+N)', { module: 'shortcuts' });
    }
    
    // Close active window (Ctrl+W)
    else if (matchesShortcut(SHORTCUTS.CLOSE_WINDOW) && activeWindowId) {
      event.preventDefault();
      dispatch(closeWindow(activeWindowId));
      logger.info(`Closed window via shortcut (Ctrl+W): ${activeWindow?.title}`, { module: 'shortcuts' });
    }
    
    // Minimize/restore active window (Alt+M)
    else if (matchesShortcut(SHORTCUTS.MINIMIZE_WINDOW) && activeWindowId) {
      event.preventDefault();
      if (activeWindow?.isMinimized) {
        dispatch(restoreWindow(activeWindowId));
        logger.info(`Restored window via shortcut (Alt+M): ${activeWindow?.title}`, { module: 'shortcuts' });
      } else {
        dispatch(minimizeWindow(activeWindowId));
        logger.info(`Minimized window via shortcut (Alt+M): ${activeWindow?.title}`, { module: 'shortcuts' });
      }
    }
    
    // Maximize/restore active window (Ctrl+M)
    else if (matchesShortcut(SHORTCUTS.MAXIMIZE_WINDOW) && activeWindowId) {
      event.preventDefault();
      if (activeWindow?.isMaximized) {
        dispatch(restoreWindow(activeWindowId));
        logger.info(`Restored window via shortcut (Ctrl+M): ${activeWindow?.title}`, { module: 'shortcuts' });
      } else {
        dispatch(maximizeWindow(activeWindowId));
        logger.info(`Maximized window via shortcut (Ctrl+M): ${activeWindow?.title}`, { module: 'shortcuts' });
      }
    }
    
    // Cycle to next window (Alt+Tab)
    else if (matchesShortcut(SHORTCUTS.NEXT_WINDOW)) {
      event.preventDefault();
      const openWindows = Object.keys(windows).filter(id => windows[id].isOpen && !windows[id].isMinimized);
      if (openWindows.length > 1) {
        const currentIndex = openWindows.indexOf(activeWindowId || '');
        const nextIndex = (currentIndex + 1) % openWindows.length;
        dispatch(focusWindow(openWindows[nextIndex]));
        logger.info(`Focused next window via shortcut (Alt+Tab): ${windows[openWindows[nextIndex]]?.title}`, { module: 'shortcuts' });
      }
    }
    
    // Cycle to previous window (Alt+Shift+Tab)
    else if (matchesShortcut(SHORTCUTS.PREVIOUS_WINDOW)) {
      event.preventDefault();
      const openWindows = Object.keys(windows).filter(id => windows[id].isOpen && !windows[id].isMinimized);
      if (openWindows.length > 1) {
        const currentIndex = openWindows.indexOf(activeWindowId || '');
        const prevIndex = (currentIndex - 1 + openWindows.length) % openWindows.length;
        dispatch(focusWindow(openWindows[prevIndex]));
        logger.info(`Focused previous window via shortcut (Alt+Shift+Tab): ${windows[openWindows[prevIndex]]?.title}`, { module: 'shortcuts' });
      }
    }
    
    // Cascade windows (Alt+Shift+Down)
    else if (matchesShortcut(SHORTCUTS.CASCADE_WINDOWS)) {
      event.preventDefault();
      dispatch(arrangeWindows({ arrangement: 'cascade' }));
      logger.info('Arranged windows in cascade via shortcut (Alt+Shift+Down)', { module: 'shortcuts' });
    }
    
    // Tile windows (Alt+Shift+Up)
    else if (matchesShortcut(SHORTCUTS.TILE_WINDOWS)) {
      event.preventDefault();
      dispatch(arrangeWindows({ arrangement: 'tile' }));
      logger.info('Arranged windows in tile via shortcut (Alt+Shift+Up)', { module: 'shortcuts' });
    }
    
    // Horizontal windows (Alt+Shift+Right)
    else if (matchesShortcut(SHORTCUTS.HORIZONTAL_WINDOWS)) {
      event.preventDefault();
      dispatch(arrangeWindows({ arrangement: 'horizontal' }));
      logger.info('Arranged windows horizontally via shortcut (Alt+Shift+Right)', { module: 'shortcuts' });
    }
    
    // Vertical windows (Alt+Shift+Left)
    else if (matchesShortcut(SHORTCUTS.VERTICAL_WINDOWS)) {
      event.preventDefault();
      dispatch(arrangeWindows({ arrangement: 'vertical' }));
      logger.info('Arranged windows vertically via shortcut (Alt+Shift+Left)', { module: 'shortcuts' });
    }
    
    // Save current layout (Alt+Shift+S)
    else if (matchesShortcut(SHORTCUTS.SAVE_LAYOUT)) {
      event.preventDefault();
      // We need to prompt for a name
      const layoutName = prompt('Enter a name for the layout:');
      if (layoutName) {
        dispatch(saveLayout({ name: layoutName }));
        logger.info(`Saved layout via shortcut (Alt+Shift+S): ${layoutName}`, { module: 'shortcuts' });
      }
    }
    
    // Apply saved layout (Alt+Shift+L)
    else if (matchesShortcut(SHORTCUTS.APPLY_LAYOUT)) {
      event.preventDefault();
      // Display layout options if we have any
      const layoutIds = Object.keys(layouts);
      if (layoutIds.length > 0) {
        const options = layoutIds.map((id, index) => `${index + 1}. ${layouts[id].name}`).join('\n');
        const choice = prompt(`Select a layout to apply:\n${options}`);
        if (choice) {
          const index = parseInt(choice, 10) - 1;
          if (!isNaN(index) && index >= 0 && index < layoutIds.length) {
            dispatch(applyLayout(layoutIds[index]));
            logger.info(`Applied layout via shortcut (Alt+Shift+L): ${layouts[layoutIds[index]].name}`, { module: 'shortcuts' });
          }
        }
      } else {
        alert('No saved layouts available');
      }
    }
    
    // Reopen last closed window (Ctrl+Z)
    else if (matchesShortcut(SHORTCUTS.REOPEN_LAST_CLOSED) && hasRecentlyClosed) {
      event.preventDefault();
      dispatch(reopenLastClosedWindow());
      logger.info('Reopened last closed window via shortcut (Ctrl+Z)', { module: 'shortcuts' });
    }
    
    // Open settings window (Ctrl+,)
    else if (matchesShortcut(SHORTCUTS.SETTINGS_WINDOW)) {
      event.preventDefault();
      dispatch(createWindow({
        title: 'Settings',
        type: 'settings',
        content: 'Settings'
      }));
      logger.info('Opened settings window via shortcut (Ctrl+,)', { module: 'shortcuts' });
    }
    
    // Open help window (F1)
    else if (matchesShortcut(SHORTCUTS.HELP_WINDOW)) {
      event.preventDefault();
      dispatch(createWindow({
        title: 'Help',
        type: 'help',
        content: 'Help'
      }));
      logger.info('Opened help window via shortcut (F1)', { module: 'shortcuts' });
    }
    
    // Focus specific window by number (Alt+1 through Alt+9)
    else if (event.altKey && NUMBER_KEYS.includes(event.key)) {
      event.preventDefault();
      const index = parseInt(event.key, 10) - 1;
      const openWindows = Object.keys(windows).filter(id => windows[id].isOpen && !windows[id].isMinimized);
      if (index >= 0 && index < openWindows.length) {
        dispatch(focusWindow(openWindows[index]));
        logger.info(`Focused window ${index + 1} via shortcut (Alt+${index + 1}): ${windows[openWindows[index]]?.title}`, { module: 'shortcuts' });
      }
    }
  }, [dispatch, windows, activeWindowId, layouts, hasRecentlyClosed, activeWindow]);
  
  // Set up and clean up keyboard event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  // Return methods for manually triggering shortcuts (useful for testing)
  return {
    createNewWindow: () => {
      dispatch(createWindow({
        title: 'New Window',
        type: 'editor',
        content: 'New window created programmatically'
      }));
    },
    closeActiveWindow: () => {
      if (activeWindowId) {
        dispatch(closeWindow(activeWindowId));
      }
    },
    toggleMinimizeWindow: () => {
      if (activeWindowId) {
        if (activeWindow?.isMinimized) {
          dispatch(restoreWindow(activeWindowId));
        } else {
          dispatch(minimizeWindow(activeWindowId));
        }
      }
    },
    toggleMaximizeWindow: () => {
      if (activeWindowId) {
        if (activeWindow?.isMaximized) {
          dispatch(restoreWindow(activeWindowId));
        } else {
          dispatch(maximizeWindow(activeWindowId));
        }
      }
    },
    arrangeWindowsCascade: () => dispatch(arrangeWindows({ arrangement: 'cascade' })),
    arrangeWindowsTile: () => dispatch(arrangeWindows({ arrangement: 'tile' })),
    arrangeWindowsHorizontal: () => dispatch(arrangeWindows({ arrangement: 'horizontal' })),
    arrangeWindowsVertical: () => dispatch(arrangeWindows({ arrangement: 'vertical' })),
    reopenLastClosed: () => hasRecentlyClosed && dispatch(reopenLastClosedWindow()),
    clearAllWindows: () => dispatch(clearAllWindows())
  };
}

export default useWindowShortcuts; 